import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import { Box, Stack, Typography as T } from "@mui/material";
import DirectoryHeader from "files/components/DirectoryHeader";
import FileRow from "files/components/FileRow";
import useExternalBuckets from "files/hooks/useExternalBuckets";
import { BASE_DIRECTORIES, resolveRouterPath, useCurrentPath } from "files/hooks/useFiles";
import FileInfo from "files/types/interfaces/fileInfo";
import { AwsIcon } from "~/assets/icons";
import ListCard from "~/components/ListCard";

interface DirectoryProps {
  files: FileInfo[];
  loading: boolean;
}

const Directory = ({ files, loading }: DirectoryProps) => {
  const directoryPath = useCurrentPath();
  const isGlobalFileContext = !useMatch("/workbenches/:workbenchId/files/*");
  const isRoot = !directoryPath;
  const { data } = useExternalBuckets();
  const showOtherLocations = !loading && isRoot && !!data?.length;

  const rows = useMemo(() => {
    const backNav = [];

    if (directoryPath) {
      backNav.push(<FileRow key="back" hideActions fileKey=".." />);
    }

    return [
      ...backNav,
      ...files.map((node) => {
        // If existing, use directory title/subtitle overrides
        const resolvedPath = resolveRouterPath(node.key, isGlobalFileContext);
        const overrides = BASE_DIRECTORIES[resolvedPath];
        return (
          <FileRow
            key={node.key}
            subtitle={overrides?.subtitle}
            fileKey={node.key}
            type={node.type}
            size={node.size}
            pendingAction={node.pending_action}
            updatedAt={node.updated_at}
            s3Uri={node.s3_uri}
          />
        );
      }),
    ];
  }, [files]);

  return (
    <Stack gap={4}>
      <ListCard header={<DirectoryHeader />} loading={loading} placeholder={{ height: 34 }}>
        {rows}
      </ListCard>
      {showOtherLocations && (
        <ListCard
          header={
            <T
              component={Box}
              variant="label"
              px={2.5}
              py={1.5}
              fontSize={13}
              fontWeight={500}
              borderBottom={1}
              borderColor="grey.300"
            >
              Other Locations
            </T>
          }
        >
          {data.map(({ bucket_name, description }) => (
            <FileRow
              key={bucket_name}
              fileKey={bucket_name}
              subtitle={description}
              badge={<AwsIcon width={24} />}
              hideActions
            />
          ))}
        </ListCard>
      )}
    </Stack>
  );
};

export default Directory;
